// eslint-disable-next-line no-restricted-imports
import type { DynamicFormFieldProps, Model } from '@webinex/dynamic-form';
import React, { useEffect } from 'react';
import { Suspense } from 'react';
import { useField } from 'formik';
import { useFirstRender } from '@app/base';
import { FormDynamicElements } from './FormDynamicElements';

export interface FormDynamicProps
  extends Omit<DynamicFormFieldProps, 'Elements'>,
    Pick<Partial<DynamicFormFieldProps>, 'Elements'> {
  readonly?: boolean;
}

const Lazy = React.lazy(async () => {
  const { DynamicForm, ModelUtil } = await import('@webinex/dynamic-form');
  const defaultElements = await FormDynamicElements.Elements();

  return {
    default: function FormDynamic(props: FormDynamicProps) {
      const { model, name, readonly, Elements = defaultElements, ...dynamicFormProps } = props;
      const [, , { setValue }] = useField(name);
      const firstRender = useFirstRender();

      useEffect(() => {
        if (firstRender) return;
        setValue(ModelUtil.initialValue(model));
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [model]);

      return (
        <DynamicForm.Field
          {...dynamicFormProps}
          Elements={Elements}
          name={name}
          model={model}
          readonly={readonly}
        />
      );
    },
  };
});

function FormDynamicComponent(props: FormDynamicProps) {
  return (
    <Suspense fallback="Loading...">
      <Lazy {...props} />
    </Suspense>
  );
}

export const FormDynamic = Object.assign(FormDynamicComponent, {
  schemaFactory: async () => {
    const { DynamicForm } = await import('@webinex/dynamic-form');
    const elements = await FormDynamicElements.Elements();
    return (model: Model) => DynamicForm.createSchema(model, elements);
  },
});
